import { useContext, useEffect, useState } from 'react';
import CustomError from '../CustomError/CustomError';
import { useSnackbar } from 'notistack';
import Box from '@mui/material/Box';
import { LinearProgress } from '@mui/material';
import moment from 'moment/moment';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { AssetsContext } from '../../DataShare/NRAssetsDataShare';
import { CONSTANTS } from '../../constants/constants';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
const { CREATED, ERROR, IN_PROGRESS, SUCCESS, ABORTED, PROCESSING } = CONSTANTS.TRANSACTION_ASSET.LOCAL_STATUS;
import { useNavigate } from 'react-router-dom';
import NRActionModal from '../NRActionModal/NRActionModal';
import { GET_ASSET_UPLOAD } from '../../graphql/queries/asset';
import useNRLazyQuery from '../../graphql/useNRLazyQuery';
import { alpha } from '@mui/material/styles';
const { TITLE_S, CANCEL_S, CONTINUE_S, MESSAGE_S } = CONSTANTS.TRANSACTION_ASSET.CONFIRMATION_MODAL;

function StatusIcon({ status }) {
  switch (status) {
    case CREATED:
      return <FileUploadOutlinedIcon />;
    case IN_PROGRESS:
      return <FileUploadOutlinedIcon />;
    case ERROR:
      return <CancelOutlinedIcon sx={{ color: 'error.main' }} />;
    case ABORTED:
      return <CancelOutlinedIcon sx={{ color: 'error.main' }} />;
    case SUCCESS:
      return <CheckCircleOutlineOutlinedIcon sx={{ color: 'success.main' }} />;
    case PROCESSING:
      return <AccessTimeOutlinedIcon />;
    default:
      return <FileDownloadOutlinedIcon />;
  }
}

function NRTaskProgress({ task, tasks }) {
  const { enqueueSnackbar } = useSnackbar();
  const [progress, setProgress] = useState(0);
  const [remainingTime, setRemainingTime] = useState(0);
  const startedTime = new Date();
  const [localUpload, setLocalUpload] = useState(task);
  const { setTasks, updateStatus } = useContext(AssetsContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (task.status !== PROCESSING) {
      if (!localUpload?.localStatus) {
        uploadFile(localUpload);
      } else if (localUpload?.localStatus === 200) {
        updateStatusLocal(CONSTANTS.TRANSACTION_ASSET.LOCAL_STATUS.PROCESSING);
      } else if (localUpload?.localStatus === 500) {
        updateStatusLocal(CONSTANTS.TRANSACTION_ASSET.LOCAL_STATUS.ERROR);
        enqueueSnackbar(<CustomError message={`${localUpload.fileToSend?.name} ${CONSTANTS.TRANSACTION_ASSET.UPLOAD_ERROR_MESSAGE}`} />, {
          variant: 'error',
          autoHideDuration: null
        });
      }
    }
  }, [localUpload]);

  const SuccessHandler = () => {
    const status = localUpload?.xhr?.status;
    if (status === 200) {
      setLocalUpload({ ...localUpload, localStatus: 200 });
    }
  };

  const handleError = () => {
    setLocalUpload({ ...localUpload, localStatus: 500 });
  };

  const ProgressHandler = e => {
    let percent = (e.loaded / e.total) * 100;
    setProgress(percent);
    const timeElapsed = new Date() - startedTime;
    const uploadSpeed = e.loaded / (timeElapsed / 1000);
    const timeRemaining = (e.total - e.loaded) / (uploadSpeed || 1);
    setRemainingTime(timeRemaining);
  };

  const updateStatusLocal = status => {
    updateStatus(status, localUpload.uploadUrl);
  };

  const addRequestRef = xhr => {
    const newTasks = [...tasks];
    const currentTaskIndex = tasks.findIndex(e => e.uploadUrl === localUpload.uploadUrl);
    newTasks[currentTaskIndex].xhr = xhr;
    setTasks(newTasks);
  };

  const uploadFile = ({ uploadUrl, fileToSend, status }) => {
    if (!!uploadUrl && !!fileToSend && status === CONSTANTS.TRANSACTION_ASSET.LOCAL_STATUS.CREATED) {
      const xhr = new XMLHttpRequest();
      addRequestRef(xhr);
      xhr.open('PUT', uploadUrl, true);
      xhr.upload.addEventListener('progress', ProgressHandler, false);
      xhr.addEventListener('load', SuccessHandler, false);
      xhr.addEventListener('error', handleError, false);
      updateStatusLocal(CONSTANTS.TRANSACTION_ASSET.LOCAL_STATUS.IN_PROGRESS);
      xhr.send(fileToSend);
    }
  };

  const abort = () => {
    if (!!localUpload.xhr) {
      localUpload.xhr.abort();
    }
  };

  const goToAssets = () => {
    navigate('/assets');
  };

  const [openModal, setOpenModal] = useState(false);
  const handleClose = () => {
    setOpenModal(false);
  };

  const continueUpload = () => {
    setOpenModal(false);
  };

  const cancelUpload = () => {
    abort();
    updateStatusLocal(CONSTANTS.TRANSACTION_ASSET.LOCAL_STATUS.ABORTED);
    setOpenModal(false);
  };

  return (
    <>
      <NRActionModal
        title={TITLE_S}
        actionLabel={CANCEL_S}
        cancelLabel={CONTINUE_S}
        content={MESSAGE_S}
        open={openModal}
        handleClose={handleClose}
        handleCancelAction={continueUpload}
        handleConfirmAction={cancelUpload}
        actionButtonProps={{ variant: 'contained', color: 'secondary' }}
        cancelButtonProps={{ variant: 'outlined', color: 'secondary' }}
        width={410}
      />
      <Box
        component="div"
        sx={theme => ({
          padding: theme.spacing(1.5, 0, 0.5, 0)
        })}
      >
        <Box
          component="div"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%'
          }}
        >
          <Box
            component="div"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%'
            }}
          >
            <Box
              component="div"
              sx={theme => ({
                padding: theme.spacing(1)
              })}
            >
              <StatusIcon status={task?.status} />
            </Box>
            <Box
              component="div"
              sx={{
                width: task?.status === IN_PROGRESS ? 'calc(100% - 40px - 46px)' : 'calc(100% - 46px)'
              }}
            >
              <Box
                component="div"
                sx={theme => ({
                  maxWidth: '100%',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  paddingBottom: theme.spacing(0.5)
                })}
              >
                <Box
                  component="div"
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden'
                  }}
                >
                  {task?.formValues?.name}
                </Box>
              </Box>
              {task?.status === IN_PROGRESS && (
                <Box
                  component="p"
                  sx={theme => ({
                    opacity: 0.6,
                    fontWeight: 400,
                    fontSize: 14,
                    cursor: 'default',
                    paddingBottom: theme.spacing(1)
                  })}
                >
                  Uploading {moment.duration(remainingTime / 60, 'minutes').humanize()} left...
                </Box>
              )}
              {task?.status === SUCCESS && (
                <Box
                  component="p"
                  sx={theme => ({
                    opacity: 0.6,
                    fontWeight: 400,
                    fontSize: 14,
                    cursor: 'default',
                    paddingBottom: theme.spacing(1)
                  })}
                >
                  Uploaded to{' '}
                  <Button
                    onClick={goToAssets}
                    variant="text"
                    sx={{
                      minWidth: 0,
                      padding: 0,
                      textTransform: 'none',
                      color: 'inherit',
                      verticalAlign: 'baseline',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontWeight: 400,
                      fontSize: 14
                    }}
                  >
                    Assets
                  </Button>
                </Box>
              )}
              {task?.status === ERROR && (
                <Box
                  component="p"
                  sx={theme => ({
                    opacity: 0.6,
                    fontWeight: 400,
                    fontSize: 14,
                    cursor: 'default',
                    paddingBottom: theme.spacing(1)
                  })}
                >
                  Failed
                </Box>
              )}
              {task?.status === ABORTED && (
                <Box
                  component="p"
                  sx={theme => ({
                    opacity: 0.6,
                    fontWeight: 400,
                    fontSize: 14,
                    cursor: 'default',
                    paddingBottom: theme.spacing(1)
                  })}
                >
                  Canceled
                </Box>
              )}
              {task?.status === PROCESSING && (
                <Box
                  component="p"
                  sx={theme => ({
                    opacity: 0.6,
                    fontWeight: 400,
                    fontSize: 14,
                    cursor: 'default',
                    paddingBottom: theme.spacing(1)
                  })}
                >
                  Processing
                </Box>
              )}
            </Box>
            {task?.status === IN_PROGRESS && (
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Box
                  component="div"
                  sx={theme => ({
                    paddingRight: theme.spacing(2),
                    marginTop: 'auto',
                    marginBottom: 'auto'
                  })}
                >
                  <IconButton aria-label="Close" onClick={() => setOpenModal(true)} size="small">
                    <CloseOutlinedIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        {task?.status === IN_PROGRESS && (
          <Box sx={{ width: '100%' }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={theme => ({
                backgroundColor: alpha(theme.palette.info.main, 0.15),
                '& .MuiLinearProgress-bar': {
                  backgroundColor: theme.palette.info.main
                }
              })}
            />
          </Box>
        )}
        {task?.status === PROCESSING && <NRProcessingTask updateStatusLocal={updateStatusLocal} localUpload={localUpload} />}
      </Box>
    </>
  );
}

export default NRTaskProgress;

function NRProcessingTask({ updateStatusLocal, localUpload }) {
  const [timeController, setTimeController] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const { setRecentlyUploadedAsset } = useContext(AssetsContext);

  const [assetUpload, isLoading, called, getAssetUpload] = useNRLazyQuery({
    query: GET_ASSET_UPLOAD,
    options: { fetchPolicy: 'no-cache', context: { version: 'v3' } },
    qlObjKey: 'assetUpload'
  });

  useEffect(() => {
    if (!!assetUpload) {
      if (!assetUpload?.uploaded) {
        if (!timeController) {
          const interval = setInterval(() => {
            getAssetUpload({ variables: { args: { uploadId: localUpload?.uploadId } } });
          }, 3000);
          setTimeController(interval);
        }
      } else {
        if (timeController) {
          clearInterval(timeController);
        }
        updateStatusLocal(CONSTANTS.TRANSACTION_ASSET.LOCAL_STATUS.SUCCESS);
        enqueueSnackbar(`${localUpload?.fileToSend?.name} ${CONSTANTS.TRANSACTION_ASSET.UPLOAD_SUCCESS_MESSAGE}`, { variant: 'success' });
        setRecentlyUploadedAsset(assetUpload?.assetId);
      }
    }
  }, [assetUpload]);

  useEffect(() => {
    getAssetUpload({ variables: { args: { uploadId: localUpload?.uploadId } } });
    return () => {
      clearInterval(timeController);
    };
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      <LinearProgress
        variant="indeterminate"
        sx={theme => ({
          backgroundColor: alpha(theme.palette.info.main, 0.15),
          '& .MuiLinearProgress-bar': {
            backgroundColor: theme.palette.info.main
          }
        })}
      />
    </Box>
  );
}
